import { isAssistantAccountType, AccountType } from "@/constants/accountTypes";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { markCampaignSeen } from "@/redux/slices/announcementTour/announcementTourSlice";
import { seenCampaignsSelector } from "@/redux/slices/announcementTour/selectors";
import { bulkImportReportSelector } from "@/redux/slices/bulkImportReport/selectors";
import { activeOperatorSelector } from "@/redux/slices/operator/selectors";
import { profileSelector } from "@/redux/slices/profile/selectors";
import { subscriptionExpiredSelector } from "@/redux/slices/ui/selectors";
import { AnnouncementTour } from "./AnnouncementTour";
import { CLIENT_PORTAL_REFRESH_CAMPAIGN_ID, getClientPortalRefreshSlides } from "./campaigns/clientPortalRefresh";
import { EA_PORTAL_REFRESH_CAMPAIGN_ID, EA_PORTAL_REFRESH_SLIDES } from "./campaigns/eaPortalRefresh";

/**
 * The portal-refresh tour: the assistant deck or the client deck, whichever the account is.
 *
 * One component rather than two mounted side by side, because an account is exactly one of the two and
 * two independently-gated modals could otherwise both decide to open.
 *
 * Mounted in `ClientPortal` ONLY. The operator dashboard already renders four uncoordinated overlays on
 * load (WelcomeOverlay, SubscriptionActiveOverlay, UpgradedPlanWelcomeModal, PlanSelectionModal), two of
 * which can already collide with each other; the client portal renders none of them. An operator deck
 * therefore needs a priority mechanism built first — none exists today.
 */
export const PortalRefreshTour = () => {
  const dispatch = useAppDispatch();
  const profile = useAppSelector(profileSelector);
  const seenCampaigns = useAppSelector(seenCampaignsSelector);
  const subscriptionExpired = useAppSelector(subscriptionExpiredSelector);
  const activeOperator = useAppSelector(activeOperatorSelector);
  // The report modal is open exactly when a bulk id is set — see BulkImportReportState.
  const { bulkId } = useAppSelector(bulkImportReportSelector);

  /*
   * `accountMetadata.type` is a copy of the authoritative `registered_as` and can drift from it — see
   * the note on isAssistantAccountType. That is tolerable here and nowhere near a permission check:
   * the cost of drift is someone getting the other deck, or none, not being let into something.
   */
  const type = profile?.accountMetadata?.type;
  const isAssistant = isAssistantAccountType(type);
  const isPassenger = type === AccountType.PASSENGER;

  /*
   * SubscriptionExpired is non-dismissable by design — it pauses the whole portal until the user logs
   * out or detaches. Rendering over it would stack a closable dialog on an unclosable one, which reads
   * as a frozen app. The bulk-import report is merely someone mid-task, but the same courtesy applies.
   *
   * Both are DEFERRALS, not suppressions: nothing is marked seen here, so the tour simply appears on
   * the next load once the blocking surface is gone.
   */
  const isBlocked = subscriptionExpired || !!bulkId;

  const campaign = isAssistant
    ? { id: EA_PORTAL_REFRESH_CAMPAIGN_ID, slides: EA_PORTAL_REFRESH_SLIDES, ctaLabel: "Continue" }
    : isPassenger
      ? { id: CLIENT_PORTAL_REFRESH_CAMPAIGN_ID, slides: getClientPortalRefreshSlides(activeOperator?.name), ctaLabel: "Get Started" }
      : null;

  if (!campaign || isBlocked || seenCampaigns.includes(campaign.id)) return null;

  return (
    <AnnouncementTour
      open
      slides={campaign.slides}
      ctaLabel={campaign.ctaLabel}
      onClose={() => dispatch(markCampaignSeen(campaign.id))}
    />
  );
};
